import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { leftChip, type Step } from './left';
import { K } from '../kabinet/atoms';
import type { PlanState } from './band';

/**
 * Планка остатка подписки в шапке (доп-ТЗ, п. 3; чеклист, «Кабинет»).
 *
 * Устройство и ступени — в `left.ts`, там же и проверки. Здесь только
 * цвета, короткая надпись и адрес: ведёт в «Счета и оплаты».
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ВЕЛИЧИНЫ — ЧАСТИЦЕЙ `k-остаток-тарифа`, А НЕ РУКАМИ.
 *
 * До этого капсула была `rk-chip` с поправками в `style`, и разошлась
 * с эталоном по четырём значениям сразу: рост 22 против 24, поле 8
 * против 12, насыщенность 500 против 600, рамка прозрачная против
 * цветной. Ни одно из четырёх не бросается в глаза поодиночке —
 * а вместе они и есть «похоже, но не то».
 *
 * Теперь величины режет сборщик частиц прямо с эталона, а руками
 * задаются ЧЕТЫРЕ ЦВЕТА СТУПЕНИ — их в частице быть не может:
 * эталон отрисован в одном состоянии, «работает», и вырезать из него
 * остальные три неоткуда.
 * ═══════════════════════════════════════════════════════════════════════
 */
const ЦВЕТА: Record<Step, { фон: string; текст: string; рамка: string; точка: string }> = {
  ok: { фон: 'var(--good-wash)', текст: 'var(--good-ink)', рамка: 'var(--good)', точка: 'var(--good)' },
  warn: { фон: 'var(--warn-wash)', текст: 'var(--warn-ink)', рамка: 'var(--warn)', точка: 'var(--warn)' },
  bad: { фон: 'var(--bad-wash)', текст: 'var(--bad-ink)', рамка: 'var(--bad)', точка: 'var(--bad)' },
  over: { фон: 'var(--bad-wash)', текст: 'var(--bad-ink)', рамка: 'var(--bad)', точка: 'var(--bad)' },
};

export function LeftChip({ wsId }: { wsId: string }) {
  const q = useQuery({
    queryKey: ['billing', wsId],
    queryFn: () => api<{ current: PlanState | null }>(`/workspaces/${wsId}/billing`),
    enabled: Boolean(wsId),
    retry: false,
    staleTime: 5 * 60_000,
  });

  // ОТКАЗ МОЛЧИТ: капсула «не удалось узнать» занимает то же место,
  // что настоящая тревога, и не говорит человеку ничего полезного
  if (q.error || q.data === undefined) return null;

  const chip = leftChip(q.data.current);
  if (chip === null) return null;

  const c = ЦВЕТА[chip.step];

  return (
    <>
      <Link
        to="plan"
        data-d="остаток-тарифа"
        title={chip.title}
        className={`${K.остатокТарифа} no-underline`}
        style={{ background: c.фон, color: c.текст, borderColor: c.рамка }}
      >
        <span aria-hidden="true" className={K.точкаОстатка} style={{ background: c.точка }} />

        {/* ═══════════════════════════════════════════════════════════
            ОДНА НАДПИСЬ НА ОБЕ ШИРИНЫ.

            Прежде их было две — «24 суток тарифа» на широком экране
            и «24 сут» на узком, — и обе стояли в разметке, лишнюю
            прятала ширина. Пакет 25.08 оставил в капсуле число
            и слово: «24 дня» помещается и в 390, и прятать нечего.
            Вторая надпись была бы копией первой. */}
        {chip.text}
      </Link>

      {/* ═══════════════════════════════════════════════════════════════
          «ПРОДЛИТЬ ТАРИФ» — РЯДОМ С ПЛАНКОЙ, А НЕ В ГЛУБИНЕ СЧЕТОВ.

          Требование пакета 25.08. Довод дизайнера дословный: продлить
          надо здесь и сейчас. Человек видит, что осталось пять дней,
          ровно в тот миг, когда смотрит в шапку; отправлять его после
          этого искать раздел значит терять его на полдороге.

          На узком экране кнопки нет: в шапке 390 стоят четыре значка
          и капсула, и шестое место занять нечем. Продление там —
          нажатием на саму капсулу, она ведёт туда же.
          ═══════════════════════════════════════════════════════════════ */}
      {chip.renew && (
        <Link
          /* ИМЯ УЗЛА — ЭТАЛОННОЕ. Без него состояние «тариф кончается»
             лежало в замере снятым и не сверялось ни разу: частица
             у кнопки была, а имени не было, и сверке было не за что
             её узнать. */
          data-d="кнопка-продлить-тариф"
          to="plan"
          className={`${K.кнопкаПродлитьТариф} no-underline rk-only-wide`}
        >
          Продлить тариф
        </Link>
      )}
    </>
  );
}
